"use client";

import ProductPage from "@/components/ProductPage";
import { KassensystemHardwareSection } from "@/components/KassensystemHardwareSection";
import ImageHighlight from "./Brautmode.png";
import ImageFeaturePOS from "./feature-pos.jpg";
import ImageFeatureCustomers from "./feature-customers.jpg";
import ImageFeatureStock from "./feature-stock.jpg";
import ImageFeatureStats from "./feature-stats.jpg";
import ImageFeatureOrder from "./feature-order.jpg";
import ImageFeatureExport from "./feature-export.jpg";
import Section from "@/components/Section";
import ImageText from "@/components/ImageText";
import { Check } from "lucide-react";
import SpecialFeaturesSection from "@/components/SpecialFeaturesSection";
import { PricingCard } from "@/components/PricingSection";
import Image from "next/image";

export default function Page() {
  return (
    <>
      <ProductPage
        productName="Cashtex Profi – Kassensystem für den Brautmodenhandel"
        hero={{
          title: "Cashtex Profi – Kassensystem für den Brautmodenhandel",
          subtitle:
            "Perfekt auf Brautmode spezialisiert: Terminplanung, individuelle Kundenprofile, flexible Artikelverwaltung und Umsatzstatistiken in Echtzeit.",
          buttonLeft: {
            title: "Unverbindliche Anfrage",
            href: "/support",
          },
          buttonRight: { title: "Preise", href: "/preise" },
          image: ImageHighlight,
          imageAlt: "Cashtex Profi Kassensystem für Brautmoden",
        }}
        highlight={{
          image: ImageHighlight,
          bullets: [
            <>
              <strong>Termin- & Anprobe-Planung</strong> inkl. Erinnerungen,
              Notizen und Abholterminen.
            </>,
            <>
              <strong>Anzahlungen & Reservierungen</strong> sauber verwalten –
              ideal für Braut- und Festmode.
            </>,
            <>
              <strong>Artikelvarianten & Anpassungen</strong> (Größen, Farben,
              Kollektionen, Änderungen) übersichtlich im System.
            </>,
            <>
              <strong>100 % rechtssicher</strong> –{" "}
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                className="text-brand-primary underline hover:opacity-80"
              >
                GoBD
              </a>
              /
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                className="text-brand-primary underline hover:opacity-80"
              >
                GDPdU
              </a>
              -konform inkl. Kassenbuch und{" "}
              <a
                href="https://www.softguide.de/funktion/datev-export"
                className="text-brand-primary underline hover:opacity-80"
              >
                DATEV
              </a>
              /
              <a
                href="https://www.softguide.de/funktion/fibu-export"
                className="text-brand-primary underline hover:opacity-80"
              >
                FIBU-Export
              </a>
              /{" "}
              <a
                href="https://www.seeburger.com/de/ressourcen/wissenswertes/was-ist-edi"
                className="text-brand-primary underline hover:opacity-80"
              >
                EDI
              </a>
              -Schnittstelle/
              <a
                href="https://www.bsi.bund.de/DE/Themen/Unternehmen-und-Organisationen/Standards-und-Zertifizierung/Schutz-vor-Manipulation-an-digitalen-Grundaufzeichnungen/Fragen-und-Antworten/fragen-und-antworten_node.html"
                className="text-brand-primary underline hover:opacity-80"
              >
                TSE
              </a>
              -konform/Schnittstelle zu Ihrem Steuerberater.
            </>,
            <>
              <strong>Finanzamt- und Gesetzkonform</strong>
            </>,
          ],
        }}
        beforeVideo={
          <>
            <Section variant="transparent" className="py-4 md:py-6 mt-12">
              <ImageText
                imagePosition="right"
                imageSlot={
                  <div className="relative overflow-hidden mt-15 rounded-2xl border border-black/10 bg-black shadow-sm h-100">
                    <video className="h-full w-full object-cover" controls>
                      <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                    </video>
                  </div>
                }
              >
                <div className="flex flex-col gap-2 mb-2">
                  <Image
                    src="/CTXr.svg"
                    alt="Cashtex"
                    width={120}
                    height={36}
                    className="object-contain"
                  />
                  <h2 className="text-3xl md:text-4xl font-semibold text-[#050a18]">
                    Kassensystem für den Brautmodenhandel
                  </h2>
                </div>

                <p className="text-slate-700 leading-relaxed">
                  Cashtex unterstützt Brautmodengeschäfte bei der Verwaltung von
                  Terminen, Kunden und individuellen Anpassungen – übersichtlich
                  und effizient im täglichen Ablauf.
                </p>

                <p className="mt-2 text-slate-700 leading-relaxed">
                  Mit integrierten Reservierungen, Anzahlungen und
                  Auswertungsmöglichkeiten behalten Sie jederzeit den Überblick
                  über Ihre Verkäufe und Termine.
                </p>

                <ul className="mt-2 space-y-2">
                  <li className="flex items-start gap-3 text-slate-700">
                    <Check className="mt-1 text-brand-primary" size={18} />
                    <span>
                      <strong>Terminplanung:</strong> Anproben und Abholtermine
                      einfach organisieren.
                    </span>
                  </li>

                  <li className="flex items-start gap-3 text-slate-700">
                    <Check className="mt-1 text-brand-primary" size={18} />
                    <span>
                      <strong>Reservierungen:</strong> Anzahlungen und Artikel
                      sicher verwalten.
                    </span>
                  </li>

                  <li className="flex items-start gap-3 text-slate-700">
                    <Check className="mt-1 text-brand-primary" size={18} />
                    <span>
                      <strong>Individuelle Anpassungen:</strong> Größen,
                      Änderungen und Varianten übersichtlich abbilden.
                    </span>
                  </li>
                </ul>
              </ImageText>
            </Section>

            <SpecialFeaturesSection
              industries={[
                {
                  title: "Brautmode",
                  standard: [
                    "Kassensystem & Verkauf",
                    "Kundenverwaltung",
                    "Artikelverwaltung",
                    "Berichte & Auswertungen",
                    "Zahlungsarten & Belegausgabe",
                    "Rabatte & Aktionspreise",
                    "Tagesabschluss",
                    "Mehrwertsteuer-Verwaltung",
                  ],
                  special: [
                    "Terminbasierte Beratung & Anproben direkt im System",
                    "Anzahlungen & Restzahlungen pro Auftrag verwalten",
                    "Änderungen & Maßanpassungen mit Statusverfolgung",
                    "Individuelle Aufträge mit Kundenwünschen und Notizen",
                    "Kundenkartei mit Maßen, Vorlieben und Bestellhistorie",
                    "Übersicht offener Aufträge und ausstehender Abholungen",
                    "Lieferantenverwaltung für Bestellungen bei Designern",
                    "Größen- und Farbmatrix für Brautkleider und Accessoires",
                  ],
                },
              ]}
            />
          </>
        }
        faqs={[
          {
            q: "Unterstützt Cashtex meine Hardware (Scanner, Drucker)?",
            a: "Ja. Gängige Barcodescanner, Bondrucker und Etikettendrucker werden unterstützt. Welche Geräte konkret kompatibel sind, klären wir gemeinsam im Onboarding.",
          },
          {
            q: "Ist Cashtex auch für kleine Brautmodenboutiquen geeignet?",
            a: "Ja. Die Lösung ist modular aufgebaut und skaliert problemlos – vom Einzelgeschäft bis zu Filialbetrieben. Sie starten klein und wachsen mit dem System.",
          },
          {
            q: "Kann ich Größen, Farben und Anpassungen verwalten?",
            a: "Ja. Cashtex unterstützt die Anforderungen im Brautmodenhandel – von Konfektionsgrößen über individuelle Änderungen bis hin zu Reservierungen und Statusverfolgung.",
          },
          {
            q: "Wie erfolgt der Export zum Steuerberater?",
            a: "Per DATEV-, Lexware- oder FIBU-Export sowie GoBD-konformer Datenbereitstellung – für eine reibungslose Übergabe ohne manuelle Nacharbeit.",
          },
          {
            q: "Wie starte ich mit der Demoversion?",
            a: "Einfach unverbindlich anfragen – wir melden uns zeitnah mit einem Terminvorschlag und richten die Demo individuell für Sie ein.",
          },
          {
            q: "Kann ich Anzahlungen und Restzahlungen pro Auftrag verwalten?",
            a: "Ja, Cashtex unterstützt die Verwaltung von Anzahlungen und Restzahlungen direkt im System – für eine transparente Auftragsabwicklung ohne manuelle Nachverfolgung.",
          },
          {
            q: "Lassen sich Kundenkartei und Maße speichern?",
            a: "Ja, jede Kundin kann mit individuellen Maßen, Vorlieben und der gesamten Bestellhistorie gespeichert werden – für eine persönliche Beratung bei jedem Besuch.",
          },
          {
            q: "Wie läuft die Einrichtung und Schulung ab?",
            a: "Nach einer persönlichen Beratung richtet unser Team das System für Sie ein. Schulungen und Support sind im Einführungsprozess inbegriffen.",
          },
        ]}
        features={[
          {
            src: ImageFeaturePOS,
            alt: "Cashtex Brautmode – Verkauf, Anzahlungen und Kasse",
            title: "Schneller Verkauf & Kassenprozess",
            desc: "Quittungen, Anzahlungen, Reservierungen und Belegdruck – ideal für Braut- und Festmodengeschäfte.",
          },
          {
            src: ImageFeatureCustomers,
            alt: "Cashtex Brautmode – Kunden- und Terminverwaltung",
            title: "Kunden- & Terminverwaltung",
            desc: "Kundenprofile, Terminplanung für Anproben, Änderungsnotizen und Erinnerungen für Abholtermine.",
          },
          {
            src: ImageFeatureStock,
            alt: "Cashtex Brautmode – Artikel- und Lagerverwaltung",
            title: "Artikel- & Lagerverwaltung",
            desc: "Größen/Farben, Kollektionen, Etikettendruck, Reservierungen und Verwaltung von Maßänderungen.",
          },
          {
            src: ImageFeatureStats,
            alt: "Cashtex Brautmode – Auswertungen und Statistiken",
            title: "Verkaufsstatistiken",
            desc: "Umsatzübersichten nach Kollektionen, Marken, Mitarbeitenden und Zeiträumen – als klare Entscheidungsgrundlage.",
          },
          {
            src: ImageFeatureOrder,
            alt: "Cashtex Brautmode – Bestellwesen und Wareneingang",
            title: "Bestellwesen & Wareneingang",
            desc: "Lieferantenbestellungen für Kollektionen, Statusverfolgung und Wareneingang – auch per Scanner.",
          },
          {
            src: ImageFeatureExport,
            alt: "Cashtex Brautmode – Kassenbuch und Export",
            title: "Kassenbuch & Export",
            desc: "Anzahlungen, Einnahmen/Ausgaben, revisionssicheres Kassenbuch sowie Exporte für DATEV, Lexware und FIBU.",
          },
        ]}
      >
        {/* PRICING CARDS */}
        <Section className="pb-24 bg-white border-y border-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
            <PricingCard
              title="Leasing ab"
              priceFull="24"
              priceCents="90"
              details="Basierend auf 0,83 € / Tag."
              details2="36 / 48 / 60 Monate Laufzeit."
              features={[
                "Warenwirtschaft ModeProfi / CashTex",
                "Voller Support & Updates & Schulungen",
                "Inklusive Kassenbuch",
                "Angebote, Order & Rechnungen",
                "1 Benutzer",
              ]}
            />

            <PricingCard
              title="Mieten ab"
              priceFull="48"
              priceCents="90"
              details="Basierend auf 1,63 € / Tag."
              details2="36 / 48 / 60 Monate Laufzeit."
              features={[
                "Warenwirtschaft ModeProfi / CashTex",
                "Voller Support & Updates & Schulungen",
                "Inklusive Kassenbuch",
                "Ideal für wachsende Unternehmen",
                "1 Benutzer",
              ]}
            />

            <PricingCard
              title="Ratenkauf ab"
              priceFull="74"
              priceCents="90"
              details="Basierend auf 2,49 € / Tag."
              details2="36 / 48 / 60 Monate Laufzeit. Restwert 10 %"
              features={[
                "Warenwirtschaft ModeProfi / CashTex",
                "Voller Support & Updates & Schulungen",
                "Inklusive Kassenbuch",
                "Eigentumserwerb nach Laufzeit",
                "1 Benutzer",
              ]}
            />
          </div>

          <div className="text-center mt-12 text-sm font-bold text-gray-900 tracking-widest uppercase">
            Alle Preise exkl. MwSt.
          </div>
        </Section>
        <KassensystemHardwareSection />
        <Section variant="gray1" className="tse-section">
          <ImageText
            imagePosition="left"
            imageSrc="/tse.png"
            imageAlt="Softwareentwicklung"
            title="TSE & Rechtssicherheit: Wir machen es einfach!"
          >
            <p>
              TSE-Pflicht oder neue Anforderungen vom Finanzamt? Kein Problem –
              wir kennen uns aus und übernehmen die Umsetzung der Technischen
              Sicherheitseinrichtung für Dich und informieren Dich über alle
              wichtigen Änderungen.
            </p>
            <ul className="ps-8">
              <li>
                <Check className="inline -ms-8 text-brand-primary" />{" "}
                <strong>Kein zusätzlicher Aufwand:</strong> Regelmäßige und
                kostenlose Updates
              </li>
              <li>
                <Check className="inline -ms-8 text-brand-primary " />{" "}
                <strong>Rechtssicher:</strong> TSE-konform – erfüllt KassenSichV
                und GoBD
              </li>
              <li>
                <Check className="inline -ms-8 text-brand-primary" />{" "}
                <strong>Zuverlässig:</strong> Datensicherheit auf deutschen
                Servern und nach höchsten Standards
              </li>
            </ul>
            <p>
              Mit der Kassensoftware von SDI arbeitest Du immer 100%
              finanzamtkonform und bist automatisch auf der sicheren Seite.
            </p>
          </ImageText>
        </Section>
      </ProductPage>
    </>
  );
}
